import React, { useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { TransactionItem } from '@/components/TransactionItem'
import { TransactionSkeleton } from '@/components/LoadingSkeletons/TransactionSkeleton'
import { EmptyTransaction } from '@/components/EmptyStates'
import {
  transactionServices,
  FormattedTransaction,
} from '@/domain/transactions'
import { useTheme } from '@/hooks/useTheme'
import { useGetWallet } from '@/domain/wallet'
import { formatDateByDeviceRegion } from '@/utils/transactionUtils'

interface TransactionsListProps {
  transactions: FormattedTransaction[]
  isLoading?: boolean
  onLoadMore?: () => void
  hasNextPage?: boolean
  isLoadingMore?: boolean
}

export const TransactionsList: React.FC<TransactionsListProps> = ({
  transactions,
  isLoading = false,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
}) => {
  const { i18n } = useTranslation()
  const theme = useTheme()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Get wallet data for currency info
  const { data: walletData } = useGetWallet()

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!hasNextPage || !onLoadMore || isLoadingMore) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0]
        if (firstEntry.isIntersecting && hasNextPage && !isLoadingMore) {
          onLoadMore()
        }
      },
      {
        root: null, // viewport
        rootMargin: '100px', // Start loading 100px before reaching the sentinel
        threshold: 0.1,
      },
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasNextPage, onLoadMore, isLoadingMore])

  // Create currency map for enhanced transaction display
  const currencyMap = useMemo(() => {
    if (!walletData?.completeCurrencies) return {}

    return walletData.completeCurrencies.reduce(
      (acc, currency) => {
        const currencyCode = currency.code?.toLowerCase()?.trim()
        if (currencyCode) {
          acc[currencyCode] = {
            icon: currency.icon,
            avatarBGRefColor: currency.avatarBGRefColor,
            name: currency.name,
            symbol: currency.symbol,
          }
        }
        return acc
      },
      {} as Record<
        string,
        {
          icon?: string
          avatarBGRefColor?: string | [string, string, ...string[]]
          name?: string
          symbol?: string
        }
      >,
    )
  }, [walletData?.completeCurrencies])

  // Enhance transactions with currency data (adapted from RN logic)
  const enhancedTransactions = useMemo(() => {
    return transactions.map((transaction) => {
      // Determine currency code based on transaction type (like in RN)
      const isConvert = transaction.type === 'swap_convert'
      const isCardPurchase = transaction.type === 'card_write_off'

      // Use the same logic as RN: for conversions use credit_amount.currency, otherwise use amount.currency
      const currencyCode = isConvert
        ? transaction.subject_swap?.credit_amount?.currency
            ?.toLowerCase()
            ?.trim()
        : transaction.amount?.currency?.toLowerCase()?.trim()

      const currencyInfo = currencyCode ? currencyMap[currencyCode] : {}

      // For conversions, also get target currency data (the currency being converted FROM)
      let targetCurrencyInfo: {
        icon?: string
        avatarBGRefColor?: string | [string, string, ...string[]]
        name?: string
        symbol?: string
      } = {}
      if (isConvert && transaction.subject_swap?.credit_amount?.currency) {
        // For conversions, the target currency is actually the original currency (amount.currency)
        const targetCurrencyCode = transaction.amount?.currency
          ?.toLowerCase()
          ?.trim()
        targetCurrencyInfo = targetCurrencyCode
          ? currencyMap[targetCurrencyCode]
          : {}
      }

      return {
        ...transaction,
        // Main currency data (what's being displayed as the primary currency)
        currencyIcon: currencyInfo?.icon,
        currencyColor: isCardPurchase
          ? theme.colors.background.cardItemRoot
          : currencyInfo?.avatarBGRefColor,
        currencyName: currencyInfo?.name,
        currencySymbol: currencyInfo?.symbol,
        // Target currency data for conversions (the original currency)
        targetCurrencyIcon: targetCurrencyInfo?.icon,
        targetCurrencyColor: targetCurrencyInfo?.avatarBGRefColor,
        targetCurrencyName: targetCurrencyInfo?.name,
        targetCurrencySymbol: targetCurrencyInfo?.symbol,
      }
    })
  }, [transactions, currencyMap, theme])

  // Group transactions by date with headers
  const transactionsWithHeaders = useMemo(() => {
    return transactionServices.addDateHeadersToTransactions(
      enhancedTransactions,
    )
  }, [enhancedTransactions])

  // Show loading skeleton for initial load
  if (isLoading && transactions.length === 0) {
    return (
      <BaseView flex={1} py={24}>
        <TransactionSkeleton count={5} />
      </BaseView>
    )
  }

  // Show empty state when no transactions and not loading
  if (transactions.length === 0 && !isLoading) {
    return (
      <BaseView flex={1}>
        <EmptyTransaction isLoadingTransactions={false} />
      </BaseView>
    )
  }

  // Don't render anything if no transactions (this will be handled by parent component)
  if (transactions.length === 0) {
    return null
  }

  return (
    <BaseView flex={1}>
      {/* Transactions List */}
      <BaseView gap={18}>
        {transactionsWithHeaders.map((item, index) => {
          if (item.type === 'date-header') {
            return (
              <BaseView
                key={`date-${item.date}`}
                px={12}
                py={8}
                style={{
                  marginTop: index > 0 ? '24px' : '0px',
                }}
              >
                <BaseText
                  fontSize={20}
                  fontFamily="semibold"
                  color="label.default"
                >
                  {item.date &&
                    formatDateByDeviceRegion({
                      date: new Date(item.date),
                      isTitle: true,
                      locale: i18n.language,
                    })}
                </BaseText>
              </BaseView>
            )
          }

          return (
            <BaseView key={item.transaction?.transaction_id}>
              <TransactionItem transaction={item.transaction!} />
            </BaseView>
          )
        })}
      </BaseView>

      {/* Infinite Scroll Sentinel and Loading */}
      {hasNextPage && (
        <BaseView>
          {/* Sentinel element for intersection observer */}
          <div ref={loadMoreRef} style={{ height: '1px', width: '100%' }} />

          {/* Loading skeleton when fetching more */}
          {isLoadingMore && (
            <BaseView py={16}>
              <TransactionSkeleton count={2} />
            </BaseView>
          )}
        </BaseView>
      )}
    </BaseView>
  )
}
